import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { processKeyName } from "../descriptorcontrols/BaseControls/utils/keyEvents";

const DROPDOWN_ROOT_ID = "react-dropdown-container";

const getDropdownRoot = () => {
    let dropdownRoot = document.getElementById(DROPDOWN_ROOT_ID);
    if(!dropdownRoot) { // then create it
        dropdownRoot = document.createElement("div");
        dropdownRoot.setAttribute("id", DROPDOWN_ROOT_ID);
        document.body.appendChild(dropdownRoot);
    }
    return dropdownRoot;
};

export default class DropdownWrapper extends React.Component {
    constructor(props) {
        super(props);

        this.onClick = this.onClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onHide = this.onHide.bind(this);
        this.onResize = this.onResize.bind(this);
        this.addListeners = this.addListeners.bind(this);
        this.removeListeners = this.removeListeners.bind(this);

        this.wrapperRef = React.createRef();
        this.dropdownRef = props.dropdownRef || React.createRef();
        this.dropdownRoot = getDropdownRoot();
        this.el = document.createElement("div"); // the portal div
        this.activeIndex = null;
        this.dropdownPositioning = null;
    }

    componentWillUnmount() {
        // remove event listeners
        this.dropdownRoot.contains(this.el) && this.dropdownRoot.removeChild(this.el);
        this.removeListeners();
    }

    componentDidMount() {
        const {initialActiveIndex} = this.props;
        this.activeIndex = initialActiveIndex !== undefined ? initialActiveIndex : 0;
        this.updateDropdownPosition();
    }

    componentDidUpdate(prevProps) {
        const {initialActiveIndex} = this.props;
        if(prevProps.initialActiveIndex !== initialActiveIndex) {
            this.activeIndex = initialActiveIndex !== undefined ? initialActiveIndex : 0;
        }
        this.updateDropdownPosition();
    }

    onClick(e) {
        // handle hide
        if($(e.target).is(this.props.ignoreClickSelector) || $(e.target).parents().is(this.props.ignoreClickSelector)) {
            return;
        }
        else if(e.target === this.wrapperRef.current) {
            // hide immediately
            this.onHide();
            return;
        }
        this.onHide();
    }

    onKeyDown(e) {
        // handle Enter/Escape/Arrow keys
        const {targetRef, ignoreSpaceKey, itemSelector, onEnterKey, activeItemClass} = this.props;
        const keyDownName = processKeyName(e.key);
        if(!["ArrowUp", "ArrowDown", "Escape", "Enter", " ", "Tab"].includes(keyDownName)) {
            return;
        }

        if([" "].includes(keyDownName) && (this.activeIndex === null || ignoreSpaceKey)) {
            return;
        }

        // has keyboard accessible items to focus/click.
        e.preventDefault();
        e.stopPropagation();

        const $target = $(targetRef.current);
        if(["Escape", "Tab"].includes(keyDownName) || $target.is(".disabled, :disabled")) {
            this.onHide();
            return;
        }

        const $dropdown = $(this.dropdownRef.current);
        const $items = $dropdown.find(itemSelector);
        if($items.length === 0) {
            // no items to show
            return;
        }


        const $activeItem = $items.eq(this.activeIndex);
        if(["Enter", " "].includes(keyDownName)) {
            if(this.activeIndex === null) {
                // no active items just hide.
                this.onHide();
                return;
            }
            else {
                // click active item
                if(onEnterKey) {
                    onEnterKey(e, this.activeIndex);
                }
                else {
                    $activeItem
                        .trigger("click") // for react
                        .get(0).click(); // for everything else
                    this.onHide();
                }
                return;
            }
        }

        if (this.activeIndex === null) {
            this.activeIndex = 0;
        }
        else if (["ArrowUp"].includes(keyDownName)) {
            this.activeIndex--; // up
        }
        else if (["ArrowDown"].includes(keyDownName)) {
            this.activeIndex++; // down
        }

        if(this.activeIndex < 0 ) { // loop to the end
            this.activeIndex = $items.length - 1;
        }
        if(this.activeIndex >= $items.length) { // loop to the beginning
            this.activeIndex = 0;
        }

        const $newActiveItem = $items.eq(this.activeIndex);
        if(activeItemClass) {
            // remove/add the active item class
            if($activeItem.hasClass(activeItemClass)) {$activeItem.removeClass(activeItemClass);}
            $newActiveItem.addClass(activeItemClass);
        }

        $dropdown.scrollTo($newActiveItem);
        $newActiveItem.trigger("focus");
    }

    onResize() {
        this.onHide();
    }

    onHide() {
        // reset local state
        this.removeListeners();
        this.props.onHide && this.props.onHide();
    }

    addListeners() {
        const {targetRef} = this.props;
        $(targetRef.current).parent().addClass("open");
        window.addEventListener("resize", this.onResize);
        window.addEventListener("keydown", this.onKeyDown);
    }

    removeListeners() {
        const {targetRef} = this.props;
        $(targetRef.current).parent().removeClass("open");
        window.removeEventListener("resize", this.onResize);
        window.removeEventListener("keydown", this.onKeyDown);
    }

    zIndexOfLayer(layer) {
        switch (layer) {
            case "navbar": return 11001;
            case "IT": return 11000; // /test/it/lib/templates/style.less
            default: return 9999;
        }
    }

    updateDropdownPosition() {
        const {targetRef, autoSize, align, dropdownLength, itemSelector, activeItemClass, layer, scrollSelector} = this.props;
        const $dropdown = $(this.dropdownRef.current);
        const $wrapper = $(this.wrapperRef.current);
        const $target = $(targetRef.current);

        const containerHeight = $(window).height();
        let maxHeight = $dropdown.outerHeight(); // height of dropdown.
        let width = autoSize || align === "fill" ? $target.outerWidth() : $dropdown.outerWidth();
        let left = $target.offset().left;
        let top = $target.offset().top + $target.outerHeight();

        // find if the target has a parent ".record-grid-container" then adjust the left and width to be from .record-grid-container
        const $gridContainer = $target.parents( ".record-grid-container" );
        if($gridContainer.length && (autoSize || align === "fill")) {
            // in a record grid! Use the $gridContainer's width and left position.
            width = $gridContainer.outerWidth();
            left = $gridContainer.offset().left;
        }

        const $items = $dropdown.find("tr, li, div");// find the items in the dropdown
        if (dropdownLength && autoSize) {
            const itemHeight = $items.length > 0 ? $items.first().outerHeight() : 37; // default height of an item
            maxHeight = itemHeight * dropdownLength;
            if ($items.length < dropdownLength) {
                maxHeight = itemHeight * $items.length;
            }
        }

        if (($target.offset().top + maxHeight + $target.outerHeight()) >= (containerHeight + $(window).scrollTop())) {
            // to big to completely show
            // find out if top or bottom has more room overall for the dropdown
            if (top >= ((containerHeight / 2) + $(window).scrollTop())) {
                // top has more room
                top = $target.offset().top - maxHeight;
                if ($(window).scrollTop() > top) {
                    // crop the dropdown.
                    maxHeight = maxHeight - Math.abs(top - $(window).scrollTop()) - 5;
                    top = $(window).scrollTop() + 5;
                }
            }
            else {
                // bottom has more room
                // crop it the dropdown.
                const overflow = ($target.offset().top + maxHeight + $target.outerHeight()) - (containerHeight + $(window).scrollTop());
                maxHeight = maxHeight - overflow - 5;
            }
        }

        switch (align) {
            case "right":
                left += $target.outerWidth() - $dropdown.outerWidth();
                break;
            case "center":
                left += ($target.outerWidth() / 2) - ($dropdown.outerWidth() / 2);
                break;
            case "fill":
            case "left":
            default:
            // defaults to left.
        }

        const styles = {
            left: left + "px",
            top: top + "px",
            maxHeight: autoSize ? maxHeight + "px" : "",
            width: width + "px",
            height: "auto",
            overflow: "auto",
        };

        // Now do the DOM stuff
        $wrapper.css({zIndex: this.zIndexOfLayer(layer)}); // set dropdowns zIndex

        if(this.activeIndex !== null && activeItemClass) {
            // focus and add active styling to activeIndex
            const $activeItem = $dropdown.find(itemSelector).eq(this.activeIndex);
            $activeItem.addClass(activeItemClass);
            $dropdown.scrollTo($activeItem); // also scroll item into view.
            $activeItem.trigger("focus");
        } else if (scrollSelector) {
            const $activeItem = $dropdown.find(scrollSelector);
            $dropdown.scrollTo($activeItem); // also scroll item into view.
        }

        if(!_.isEqual(styles, this.dropdownPositioning)) {
            // only update if different.
            $dropdown.css(styles);
        }
        this.dropdownPositioning = styles;
    }

    render() {
        const {children} = this.props;
        if(!this.dropdownRoot.contains(this.el)) {
            // add listener
            this.dropdownRoot.appendChild(this.el);
            this.addListeners();
        }

        let kid = children;
        if(!this.props.dropdownRef) {
            // setup a dropdownRef for the provided child.
            kid = React.Children.toArray(children);
            if(kid.length > 1) {
                throw new Error("DropdownHelper only supports a single child.");
            }
            else {
                kid = React.cloneElement(kid[0], { ref: this.dropdownRef });
            }
        }

        const content = (
            <div
                ref={this.wrapperRef}
                className="dropdown-wrapper open"
                onClick={this.onClick}
                onKeyDown={this.onKeyDown}
            >
                {kid}
            </div>
        );

        return ReactDOM.createPortal(
            content,
            this.el,
        );
    }
}

DropdownWrapper.defaultProps = {
    appendTo: "body", dropdownLength: 8,
    autoSize: true,
    align: "left",
    layer: "default",
    toggle: true,
    itemSelector: "li:not(.disabled):visible a",
    ignoreClickSelector: "",
    initialActiveIndex: null,
    activeItemClass: "",
    scrollSelector: null,
    ignoreSpaceKey: false,
};

DropdownWrapper.propTypes = {
    targetRef: PropTypes.object.isRequired,
    dropdownRef: PropTypes.object,
    children: PropTypes.any,
    options: PropTypes.object,
    onHide: PropTypes.func.isRequired,
    appendTo: PropTypes.string,
    dropdownLength: PropTypes.number,
    autoSize: PropTypes.bool,
    align: PropTypes.string,
    layer: PropTypes.string,
    toggle: PropTypes.bool,
    onEnterKey: PropTypes.func,
    itemSelector: PropTypes.string,
    ignoreClickSelector: PropTypes.string,
    initialActiveIndex: PropTypes.number,
    activeItemClass: PropTypes.string,
    scrollSelector: PropTypes.string,
    ignoreSpaceKey: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./app/lib/common/components/DropdownWrapper.js